import { inject, injectable, interfaces } from 'inversify';
import { ACDLError } from '../../models/error.model';
import { IMainApiService } from '../../services/api.service';
import { IApiService } from '../../services/api.service.base';
import { IImage } from '../annotation/annotations.interface';
import { FreeAccessMode } from './freeAccess.store';

export interface IFreeAccessPaging {
  wasChanged: boolean;
  checkDate: string;
  items: IFreeAccessPagingItem[];
}

export interface IFreeAccessPagingItem {
  imageId: string;
  index: number;
  size: number;
}

export interface IGetImagesPagingPayload {
  projectId: string;
  orderBy: string;
  orderType: string;
  lastUpdate?: string;
  filterId: string;
}

export interface IFreeAccessApiService {
  getImageLockInfo: (projectId: string, imageId: string) => Promise<string | ACDLError>;
  getImageInfoToFreeAccess: (projectId: string, imageId: string) => Promise<IImage | ACDLError>;
  getFreeAccessPaging: (payload: IGetImagesPagingPayload) => Promise<IFreeAccessPaging | ACDLError>;
  reLockImageAsync: (imageId: string, projectId: string, mode: FreeAccessMode) => Promise<void | ACDLError>;
  lockImageAsync: (imageId: string, projectId: string, mode: FreeAccessMode) => Promise<void | ACDLError>;
}

export namespace IFreeAccessApiService {
  export const $: interfaces.ServiceIdentifier<IFreeAccessApiService> = Symbol('FREE_ACCESS_API_SERVICE');
}
@injectable()
export class FreeAccessApiService implements IFreeAccessApiService {
  constructor(@inject(IMainApiService.$) private readonly apiService: IApiService) {}

  getImageLockInfo = async (projectId: string, imageId: string): Promise<string | ACDLError> =>
    this.apiService.getAsync(`/images/GetImageLockInfo?projectId=${projectId}&imageId=${imageId}`);

  getImageInfoToFreeAccess = async (projectId: string, imageId: string): Promise<IImage | ACDLError> =>
    this.apiService.getAsync(`/Images/GetImageToFreeAccess?projectId=${projectId}&imageId=${imageId}`);

  getFreeAccessPaging = async (payload: IGetImagesPagingPayload): Promise<IFreeAccessPaging | ACDLError> =>
    this.apiService.postAsync<IFreeAccessPaging>('/Projects/FreeAccessPaging', payload);

  reLockImageAsync = async (imageId: string, projectId: string, mode: FreeAccessMode): Promise<void | ACDLError> =>
    this.apiService.postAsync('/images/ReLockImageForFreeAccess', { imageId, projectId, mode });

  lockImageAsync = async (imageId: string, projectId: string, mode: FreeAccessMode): Promise<void | ACDLError> =>
    this.apiService.postAsync('/images/LockImageForFreeAccess', { imageId, projectId, mode });
}
